type OfflineDiagnosticLevel = 'info' | 'warn' | 'error';

type OfflineDiagnosticEntry = {
  timestamp: string;
  level: OfflineDiagnosticLevel;
  message: string;
  details?: string;
};

const STORAGE_KEY = 'mlst-offline-diagnostics-v1';
const MAX_ENTRIES = 80;

function sanitizeValue(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack ? value.stack.split('\n').slice(0, 6).join('\n') : undefined,
    };
  }

  if (typeof value === 'string') {
    return value.length > 320 ? `${value.slice(0, 317)}...` : value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    const limited = value.slice(0, 8).map((item) => sanitizeValue(item, depth + 1));
    if (value.length > 8) {
      limited.push(`... ${value.length - 8} more items`);
    }
    return limited;
  }

  if (typeof value === 'object') {
    if (depth >= 2) {
      return '[object truncated]';
    }

    const entries = Object.entries(value as Record<string, unknown>).slice(0, 12);
    const result: Record<string, unknown> = {};
    entries.forEach(([key, entryValue]) => {
      result[key] = sanitizeValue(entryValue, depth + 1);
    });
    const totalKeys = Object.keys(value as Record<string, unknown>).length;
    if (totalKeys > entries.length) {
      result.__truncatedKeys = totalKeys - entries.length;
    }
    return result;
  }

  return String(value);
}

function stringifyDetails(details: unknown): string | undefined {
  if (details === undefined) {
    return undefined;
  }

  if (typeof details === 'string') {
    return details;
  }

  try {
    return JSON.stringify(sanitizeValue(details), null, 2);
  } catch (error) {
    return `Failed to serialize details: ${error instanceof Error ? error.message : String(error)}`;
  }
}

function readEntries(): OfflineDiagnosticEntry[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeEntries(entries: OfflineDiagnosticEntry[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    // Ignore diagnostics persistence failures.
  }
}

export function clearOfflineDiagnostics(): void {
  writeEntries([]);
}

export function recordOfflineDiagnostic(
  level: OfflineDiagnosticLevel,
  message: string,
  details?: unknown
): void {
  const entries = readEntries();
  entries.push({
    timestamp: new Date().toISOString(),
    level,
    message,
    details: stringifyDetails(details),
  });
  writeEntries(entries);
}

export function buildImmediateOfflineDiagnosticsReport(extra?: Record<string, unknown>): string {
  const lines: string[] = [
    'MLST Offline Diagnostics',
    `Generated: ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `Online: ${navigator.onLine}`,
    `Visibility: ${document.visibilityState}`,
    `User Agent: ${navigator.userAgent}`,
  ];

  if ('serviceWorker' in navigator) {
    lines.push(`Service Worker Controlled: ${navigator.serviceWorker.controller ? 'yes' : 'no'}`);
    if (navigator.serviceWorker.controller?.scriptURL) {
      lines.push(`Service Worker Script: ${navigator.serviceWorker.controller.scriptURL}`);
    }
  }

  if (extra && Object.keys(extra).length > 0) {
    lines.push('');
    lines.push('Context:');
    lines.push(stringifyDetails(extra) || '(none)');
  }

  lines.push('');
  lines.push('Entries:');

  const entries = readEntries();
  if (entries.length === 0) {
    lines.push('(no offline diagnostics captured)');
  } else {
    entries.forEach((entry) => {
      lines.push(`[${entry.timestamp}] ${entry.level.toUpperCase()} ${entry.message}`);
      if (entry.details) {
        lines.push(entry.details);
      }
    });
  }

  return lines.join('\n');
}

export async function buildOfflineDiagnosticsReport(extra?: Record<string, unknown>): Promise<string> {
  const baseReport = buildImmediateOfflineDiagnosticsReport(extra);

  if (!('storage' in navigator) || typeof navigator.storage?.estimate !== 'function') {
    return baseReport;
  }

  try {
    const estimate = await navigator.storage.estimate();
    const storageLines = [
      `Storage Usage: ${estimate.usage ?? 'unknown'}`,
      `Storage Quota: ${estimate.quota ?? 'unknown'}`,
    ];
    return baseReport.replace('\nEntries:\n', `\n${storageLines.join('\n')}\n\nEntries:\n`);
  } catch (error) {
    const storageError = `Storage Estimate Error: ${error instanceof Error ? error.message : String(error)}`;
    return baseReport.replace('\nEntries:\n', `\n${storageError}\n\nEntries:\n`);
  }
}
